import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Button, Card, Spin, Alert, message, Form, Modal } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../../utils/socketio/SocketWrapper';
import { useAuth } from '../../utils/Auth/AuthWrapper';
import { taskAccess } from '../../services/task_access';
import { user_login } from '../../services/user';
import { LoginModal } from './component/LoginModal';

export const TaskAccessManagement = createContext(false);
export const TaskOwnerAccess = createContext(false);
export const TaskSubmissionAccess = createContext(true);
export const TaskRegistrationRefresh = createContext<() => Promise<void>>(async () => {});

export default function TaskAccess({ children, requestId, onClose }: { children?: React.ReactNode; requestId?: string; onClose?: () => void }) {
  const params = useParams();
  const id = requestId || params.id;
  const navigate = useNavigate();
  const auth = useAuth();
  const { socket, isConnected } = useSocket() as any;
  const [state, setState] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginForm] = Form.useForm();
  const inviteToken = () => new URLSearchParams(window.location.hash.slice(1)).get('invite') || new URLSearchParams(window.location.search).get('invite');
  const refresh = useCallback(async () => {
    setError('');
    try {
      const result = await taskAccess('status', { task_id: id });
      setState(result);

    } catch (e: any) { setState(null); setError(e.message); }
  }, [id, auth.user?.user_id]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!state) return;
    if (!requestId && !state.allowed) {
      const token = inviteToken();
      navigate({ pathname: '/', search: `?access_task=${encodeURIComponent(id || '')}`, hash: token ? `#invite=${encodeURIComponent(token)}` : '' }, { replace: true });
    } else if (requestId && state.allowed) {
      navigate(`/task/detail/${encodeURIComponent(requestId)}`, { replace: true });
    }
  }, [state, requestId, id, navigate]);
  useEffect(() => {
    const connection = socket?.current;
    if (!connection || !isConnected) return;
    const revoked = (event: { task_id: string }) => {
      if (event.task_id !== id) return;
      setState((previous: any) => previous && ({ ...previous, allowed: false, is_member: false, can_submit: false, can_register: false, status: 'rejected' }));
      refresh();
    };
    connection.on('task:access-revoked', revoked);
    return () => { connection.off('task:access-revoked', revoked); };
  }, [socket, isConnected, id, refresh]);

  const action = async (name: string, data: any = {}) => {
    setBusy(true);
    try {
      const token = name === 'request' ? inviteToken() : null;
      await taskAccess(token ? 'redeem' : name, { task_id: id, ...data, ...(token && { token }) });
      if (token) {
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        const fragment = new URLSearchParams(url.hash.slice(1));
        fragment.delete('invite');
        url.hash = fragment.toString();
        window.history.replaceState(null, '', url.toString());
      }
      await refresh();
      message.success('บันทึกเรียบร้อย');
    } catch (e: any) { message.error(e.message); }
    finally { setBusy(false); }
  };
  const login = async (values: any) => {
    setBusy(true);
    try {
      const response = await user_login({ user_email: values.email, password: values.password });
      if (response.message !== 'success' || response.status !== 1) throw new Error('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
      const destination = window.location.href;
      await auth.login(response.result);
      window.location.assign(destination);
    } catch (e: any) { message.error(e.message); }
    finally { setBusy(false); }
  };
  const download = async (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor || event.defaultPrevented) return;
    const url = new URL(anchor.href, window.location.href);
    if (!/^\/(?:api\/)?(?:data|image)\/(?:datasets|medias)\//.test(url.pathname)) return;
    if (!state?.authenticated) return;
    event.preventDefault();
    try {
      const result = await taskAccess('file-url', { task_id: id, url: url.href });
      const link = document.createElement('a');
      link.href = result.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) { message.error(e.message); }
  };
  if (error) return requestId ? <Modal open title="ขออนุญาติเข้าถึง task" onCancel={onClose} footer={<Button onClick={onClose}>ยกเลิก</Button>}><Alert type="warning" message={error} /><Button onClick={refresh}>ลองใหม่</Button></Modal> : <Card><Alert type="warning" message={error} /><Button onClick={refresh}>ลองใหม่</Button></Card>;
  if (!state) return requestId ? <Modal open title="ขออนุญาติเข้าถึง task" onCancel={onClose} footer={<Button onClick={onClose}>ยกเลิก</Button>}><Spin /></Modal> : <Spin />;
  const loginModal = <LoginModal isOpen={loginOpen} submitting={busy} form={loginForm} onCancel={() => setLoginOpen(false)} onLogin={login} />;
  if (!state.allowed && !requestId) return <Spin />;
  if (!state.allowed) return <><Modal open title={state.preview?.name || "ขออนุญาติเข้าถึง task"} onCancel={onClose} closable={!busy} maskClosable={!busy} keyboard={!busy} footer={[
    <Button key="cancel" disabled={busy} onClick={onClose}>ยกเลิก</Button>,
    ...(!state.authenticated ? [<Button key="login" type="primary" onClick={() => setLoginOpen(true)}>เข้าสู่ระบบ</Button>] : state.status === 'pending' ? [] : [<Button key="request" type="primary" loading={busy} onClick={() => action('request')}>{state.status === 'rejected' ? 'ขอสิทธิ์อีกครั้ง' : 'ขออนุญาติเข้าถึง'}</Button>]),
  ]}>
    <p>คุณดูข้อมูลเบื้องต้นของ Task ได้ แต่เนื้อหา ไฟล์ และการส่งงานจะเปิดหลัง Super Admin เจ้าของ Task อนุมัติเท่านั้น</p>
    {state.status === "rejected" && <Alert type="warning" message="คำขอก่อนหน้าถูกปฏิเสธ คุณสามารถกดขอสิทธิ์ใหม่ได้" style={{ marginBottom: 12 }} />}
    {!state.authenticated ? <>
      {inviteToken() && <p>เข้าสู่ระบบ แล้วกดขอสิทธิ์เข้าถึงจากหน้านี้</p>}
    </> : state.status === 'pending' ? <Alert type="info" message="ส่งคำขอแล้ว กำลังรออนุมัติ" /> :
      null}
    <Button disabled={busy} onClick={refresh}>ตรวจสอบสิทธิ์อีกครั้ง</Button>
  </Modal>{loginModal}</>;
  return <>
    {state.can_submit === false && (state.can_register ? <Alert type="info" message="ได้รับสิทธิ์ใช้งานแล้ว กรุณากด Register ก่อนสร้างทีมและส่งงาน" /> : <Alert type="warning" message="ต้องได้รับอนุมัติจากเจ้าของ Task ก่อนส่งงาน" action={<Button disabled={busy || state.status === 'pending'} onClick={() => action('request')}>{state.status === 'pending' ? 'รออนุมัติ' : 'ขอสิทธิ์ส่งงาน'}</Button>} />)}
    <TaskAccessManagement.Provider value={state.can_approve === true}><TaskOwnerAccess.Provider value={state.can_delegate === true}><TaskRegistrationRefresh.Provider value={refresh}><TaskSubmissionAccess.Provider value={state.can_submit !== false}><div onClickCapture={download}>{children}</div></TaskSubmissionAccess.Provider></TaskRegistrationRefresh.Provider></TaskOwnerAccess.Provider></TaskAccessManagement.Provider>
    {loginModal}
  </>;
}
